<script setup lang="ts">
import { usePrimeVue } from 'primevue/config';
import { computed } from 'vue';

import type {
  CommissionCountriesTabsEmits,
  CommissionCountriesTabsProps,
} from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionCountriesTabs/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import VFormErrorTag from '@modules/forms/components/VFormErrorTag/VFormErrorTag.vue';
import { isNullOrUndefined } from '@utils/guards';

const props = defineProps<CommissionCountriesTabsProps>();

const emit = defineEmits<CommissionCountriesTabsEmits>();

const primevue = usePrimeVue();

const tabsForSelect = computed<IFormSelectInputOption<number>[]>(() => props.tabs?.map((item) => ({
  label: item.label || item.emptyLabel || '',
  value: item.index,
})));

const activeTab = computed(() => props.tabs?.find(({ isActive }) => isActive));

const selectLabel = computed(() => primevue.config.locale?.choose);

function onToggle(index: number): void {
  emit('expand-toggle', index);
}

function onRemove(index: number): void {
  emit('remove', index);
}

function onSelectItem(index?: number): void {
  if (isNullOrUndefined(index))
    return;

  emit('select', index);
}
</script>

<template>
  <div v-data-qa-auto-id="'CommissionCountriesMobileTabs'" class="mb-8">
    <div class="flex items-center gap-2">
      <div class="w-full input-max-width">
        <VFormSelectInput
          :options="tabsForSelect"
          :value="activeTab?.index"
          :label="selectLabel"
          is-full-width
          hide-errors
          @change="onSelectItem"
        />
      </div>
      <div
        v-if="activeTab && activeTab.index !== 0"
        class="flex-none"
      >
        <VIconButton
          :icon="activeTab.expanded ? 'pi-angle-up' : 'pi-angle-down'"
          @click="onToggle(activeTab.index)"
        />
      </div>
      <div
        v-if="activeTab && activeTab.index !== 0 && !readonly"
        class="flex-none"
      >
        <VIconButton
          icon="pi-times"
          @click="onRemove(activeTab.index)"
        />
      </div>
    </div>
    <div class="mt-4 text-center">
      <VFormErrorTag
        class="min-w-[140px]"
        :errors="errors"
      />
    </div>
    <div
      v-if="!readonly"
      class="mt-4"
    >
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FILTER_ADD_NEW_FILTER')"
        icon="pi pi-plus-circle"
        text
        @click="emit('add')"
      />
    </div>
  </div>
</template>
